
import Component from 'vue-class-component';
import Vue from 'vue';
import { Prop, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { LoanProjectionChartColors } from '@/loan/enums/loan-projection-chart-colors';
import { LoanProjectionChartService } from '@/loan/services/projection-chart';
import { ViewportDimensions } from '@/core/types/viewport-dimensions';
import { Debouncer } from '@/core/types/debouncer';
import { LoanProjectionChartContext } from '@/loan/types/loan-projection-chart-context';
import { cloneDeep } from 'lodash';
import Chart from 'chart.js';
import { UtilHelper } from '@/util/helper';
import LoanTakeBackExplainModal from '@/loan/components/LoanTakeBackExplainModal.vue';

const coreNamespace = namespace('core');
const TEST_ID = 'Kloans-Loan-LoanProjectionChart';

@Component({
	components: {
		LoanTakeBackExplainModal,
	},
})
export default class LoanProjectionChart extends Vue {
	@Prop({ required: true })
	public context!: LoanProjectionChartContext;

	@Prop({ default: TEST_ID })
	public dataTestid!: string;

	@Prop({ default: false })
	public isLoading!: boolean;

	public isLoadingLocal = false;

	@Prop({ default: 2000 })
	public loaderDelay!: number;

	@Prop({ required: true })
	public startBalance!: number;

	public viewportWatchDebouncer!: Debouncer<ViewportDimensions>;

	@Prop({ default: true })
	public withPayoffDialog!: boolean;

	@Prop({ default: true })
	public withLoader!: boolean;

	@Watch('isLoading')
	public async watchIsLoading(isLoading: boolean): Promise<void> {
		if (!this.isLoading && this.withLoader) {
			await UtilHelper.sleep(this.loaderDelay);
		}

		this.isLoadingLocal = isLoading;
	}

	@Watch('context')
	public watchContext(): void {
		if (this.isMounted) {
			this.renderChart();
		}
	}

	@coreNamespace.State('viewportDimensions')
	public viewportDimensions!: ViewportDimensions;

	@Watch('viewportDimensions')
	public watchViewportDimensions(dimensions: ViewportDimensions): void {
		this.viewportWatchDebouncer.debounce(dimensions);
	}

	private isMounted = false;

	public showModal: boolean = false;

	private originalChartInstance!: Chart;

	private projectionChartInstance!: Chart;

	mounted(): void {
		this.isMounted = true;

		this.viewportWatchDebouncer = new Debouncer({
			callback: this.renderChart,
			debounceTime: 2000,
			skipIfSame: true,
		});

		this.renderChart();
	}

	public async renderChart(): Promise<void> {
		if (!this.context) {
			return;
		}

		const projection = cloneDeep(this.context.projection);
		const originalCanvas = this.$refs.originalCanvas as HTMLCanvasElement;
		const originalCanvasContext = originalCanvas.getContext('2d');
		const projectionCanvas = this.$refs.projectionCanvas as HTMLCanvasElement;
		const projectionCanvasContext = projectionCanvas.getContext('2d');

		// if (this.originalChartInstance) {
		// 	this.originalChartInstance.destroy();
		// }

		// if (this.projectionChartInstance) {
		// 	this.projectionChartInstance.destroy();
		// }

		if (!originalCanvasContext || !projectionCanvasContext) {
			return;
		}

		// originalCanvasContext.clearRect(0, 0, originalCanvas.width, originalCanvas.height);
		// projectionCanvasContext.clearRect(0, 0, projectionCanvas.width, projectionCanvas.height);

		const currentPeriodIndex = projection.currentProjectionPeriodIndex;
		const maxBalanceAmount = LoanProjectionChartService.calculateMaxYAxesAmount(projection.projectionPeriods);

		const historicalPeriods = LoanProjectionChartService.getHistoricalPeriods({
			currentPeriodIndex,
			periods: projection.projectionPeriods,
		});

		const futurePeriods = LoanProjectionChartService.getFuturePeriods({
			currentPeriodIndex,
			periods: projection.projectionPeriods,
		});

		const historicalGradient = LoanProjectionChartService.createGradient({
			canvasContext: projectionCanvasContext,
			darkColor: LoanProjectionChartColors.DARK_GREEN,
			lightColor: LoanProjectionChartColors.LIGHT_GREEN,
		});

		const futureGradient = LoanProjectionChartService.createGradient({
			canvasContext: projectionCanvasContext,
			darkColor: LoanProjectionChartColors.DARK_GRAY,
			lightColor: LoanProjectionChartColors.LIGHT_GRAY,
		});

		const historicalChartDatasetData = LoanProjectionChartService.getHistoricalChartDatasetData(historicalPeriods);

		const futureChartDatasetData = LoanProjectionChartService.getFutureChartDatasetData({
			futurePeriods,
			historicalPeriods,
		});

		const historicalChartDataset = LoanProjectionChartService.createChartDataset({
			backgroundColor: historicalGradient,
			label: 'Historical',
			data: historicalChartDatasetData,
		});

		const futureChartDataset = LoanProjectionChartService.createChartDataset({
			backgroundColor: futureGradient,
			label: 'Future',
			data: futureChartDatasetData,
		});

		const originalChartDatasetData = LoanProjectionChartService.getOriginalChartDatasetData(
			projection.projectionPeriods,
		);

		const originalChartDataset = LoanProjectionChartService.createChartDataset({
			backgroundColor: LoanProjectionChartColors.ORIGINAL_BG,
			label: 'Original',
			data: originalChartDatasetData,
			borderColor: LoanProjectionChartColors.ORIGINAL_BORDER,
			borderWidth: 1,
		});

		LoanProjectionChartService.setProjectionChartAsChartType({
			canvasContainer: this.$refs.canvasContainer as HTMLElement,
			periods: projection.projectionPeriods,
			withPayoffDialog: this.withPayoffDialog,
		});

		this.originalChartInstance = LoanProjectionChartService.createOriginalChartInstance({
			canvasContext: originalCanvasContext,
			dataset: originalChartDataset,
			maxBalanceAmount,
			periods: projection.projectionPeriods,
		});

		// await UtilHelper.sleep(1000);

		this.projectionChartInstance = LoanProjectionChartService.createProjectionChartInstance({
			canvasContext: projectionCanvasContext,
			currentPeriodIndex,
			futureDataset: futureChartDataset,
			historicalDataset: historicalChartDataset,
			maxBalanceAmount,
			periods: projection.projectionPeriods,
			startBalance: this.startBalance,
		});

		if (!this.context.isOriginal && this.withPayoffDialog) {
			LoanProjectionChartService.showPayoffDialog(this.$refs.canvasContainer as HTMLElement);
		}
	}
}
